import { createContext, useContext, useState, useEffect } from 'react';

const ShopContext = createContext();

const CART_STORAGE_KEY = 'dj_shadow_cart';

const readCartFromStorage = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Failed to parse cart:', e);
    return [];
  }
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

export const ShopProvider = ({ children }) => {
  // Lazy initialiser runs once BEFORE any render / effect, so the cart
  // is always hydrated from localStorage before the persist effect can
  // ever fire. This is StrictMode-safe (unlike a useRef guard which
  // fails because effects in the same commit see the synchronous ref
  // update but not the not-yet-rendered state).
  const [cart, setCart] = useState(readCartFromStorage);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Persist cart whenever it changes. First run writes the hydrated
  // value back to LS (no-op semantically) — the previous bug wrote []
  // over the saved cart because state started at [] and only got
  // hydrated later inside an effect.
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, variant, quantity = 1) => {
    setCart(prevCart => {
      const existingIndex = prevCart.findIndex(
        item => item.product_id === product.id && 
                item.size === variant.size && 
                item.color === variant.color
      );

      if (existingIndex >= 0) {
        // Update quantity
        const newCart = [...prevCart];
        newCart[existingIndex].quantity += quantity;
        return newCart;
      } else {
        // Add new item
        return [...prevCart, {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          size: variant.size,
          color: variant.color,
          quantity
        }];
      }
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCart(prevCart => prevCart.filter(
      item => !(item.product_id === productId && item.size === size && item.color === color)
    ));
  };

  const updateQuantity = (productId, size, color, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, size, color);
      return;
    }
    
    setCart(prevCart => prevCart.map(item => {
      if (item.product_id === productId && item.size === size && item.color === color) {
        return { ...item, quantity };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <ShopContext.Provider value={{
      cart,
      cartTotal,
      cartCount,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart
    }}>
      {children}
    </ShopContext.Provider>
  );
};
